import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, ShoppingBag, Gift } from "lucide-react";
import axios from "axios";
import { useCart } from "../contexts/CartContext";
import ProductImage from "./ProductImage";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductModal = ({ product, isOpen, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const { addItem, triggerFly, items } = useCart();
  const buyBtnRef = useRef(null);

  // Reward-per-piece from the customer's tier (A+ → 150, C+ → 75, B+ → 50).
  // Used to show a red "🎁 خصم لك: X د.ع" badge inside the buy modal —
  // ONLY for products that DON'T set `excludes_rewards=true`. Requested
  // by admin Feb 17, 2026 so items with no discount stop showing the
  // savings line.
  const [rewardPerItem, setRewardPerItem] = useState(0);
  useEffect(() => {
    // Fetch once on first open; subsequent opens reuse the cached value.
    if (!isOpen || rewardPerItem > 0) return;
    axios.get(`${API}/rewards/me`)
      .then((r) => setRewardPerItem(Number(r.data?.reward_per_item || 0)))
      .catch(() => { /* customer not logged in / no tier — hide badge */ });
  }, [isOpen]);

  // Reset quantity when product changes
  useEffect(() => {
    setQuantity(1);
  }, [product?.id]);

  // How many already in the cart for THIS product
  const inCart = items?.find((it) => it.product_id === product?.id)?.quantity || 0;
  const remainingStock = Math.max(0, Number(product?.stock ?? 0) - inCart);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!product) return null;

  const handleAdd = () => {
    const rect = buyBtnRef.current?.getBoundingClientRect();
    addItem(product, quantity);
    if (rect) triggerFly(rect, product);
    toast.success(`تمت إضافة ${quantity} ${product.name_ar} إلى السلة`);
    setQuantity(1);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          onClick={onClose}
          data-testid="product-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="product-modal"
            className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <button
              onClick={onClose}
              data-testid="modal-close"
              aria-label="Close"
              className="absolute top-3 left-3 z-10 p-2 bg-white/90 backdrop-blur rounded-full hover:bg-white shadow-md transition-colors"
            >
              <X className="w-5 h-5 text-[#2D4A3E]" />
            </button>

            {/* Product Visual */}
            <div
              className="h-44 sm:h-56 flex items-center justify-center p-6 relative overflow-hidden"
              style={{ backgroundColor: product.color }}
            >
              {(product.image_path || product.image_path2) ? (
                <ProductImage
                  image={product.image_path}
                  image2={product.image_path2}
                  alt={product.name_ar}
                  className="w-full h-full"
                />
              ) : (
                <motion.h2
                  className="font-heading font-bold text-2xl sm:text-3xl text-center text-[#111814] relative z-10"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {product.name_ar}
                </motion.h2>
              )}
            </div>

            {/* Details */}
            <div className="p-5 sm:p-6 space-y-4">
              <div>
                <p className="text-xs sm:text-sm text-[#5C6E64]">{product.name}</p>
                <p className="font-heading font-bold text-2xl sm:text-3xl text-[#2D4A3E] mt-1" dir="ltr">
                  {Number(product.price).toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع
                </p>
              </div>

              {/* Quantity Selector */}
              <div className="bg-[#F3F6F4] rounded-2xl p-3 flex items-center justify-between">
                <span className="font-medium text-sm sm:text-base text-[#111814]">
                  الكمية
                  <span className="text-[10px] text-[#5C6E64] mr-1" dir="ltr">
                    (المتوفر: {remainingStock}{inCart > 0 ? ` · في السلة: ${inCart}` : ""})
                  </span>
                </span>
                <div className="flex items-center gap-3">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="quantity-decrease"
                    aria-label="Decrease quantity"
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white border border-[#E4EBE6] flex items-center justify-center hover:bg-[#E4EBE6] transition-colors"
                  >
                    <Minus className="w-4 h-4 text-[#2D4A3E]" />
                  </motion.button>
                  <span
                    data-testid="quantity-value"
                    className="font-bold text-lg sm:text-xl w-8 sm:w-10 text-center text-[#111814]"
                  >
                    {quantity}
                  </span>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      if (quantity >= remainingStock) {
                        toast.error(`هذا المنتج غير متوفّر حاليّاً بكمية أكثر من ${remainingStock}`);
                        return;
                      }
                      setQuantity(quantity + 1);
                    }}
                    disabled={quantity >= remainingStock}
                    data-testid="quantity-increase"
                    aria-label="Increase quantity"
                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors ${
                      quantity >= remainingStock
                        ? "bg-[#BAE6FD] cursor-not-allowed"
                        : "bg-[#2D4A3E] hover:bg-[#233A30]"
                    }`}
                  >
                    <Plus className="w-4 h-4 text-white" />
                  </motion.button>
                </div>
              </div>

              {remainingStock === 0 ? (
                <div data-testid="modal-stock-warning" className="bg-[#FEE2E2] border border-[#E85D45] text-[#E85D45] rounded-xl px-3 py-2 text-xs text-center font-bold">
                  هذا المنتج غير متوفّر حاليّاً
                </div>
              ) : quantity >= remainingStock && (
                <div data-testid="modal-stock-warning" className="bg-[#FFF8EC] border border-[#F5A623] text-[#a06e10] rounded-xl px-3 py-2 text-xs text-center">
                  وصلت إلى الحدّ الأقصى المتوفّر في المخزن — هذا المنتج غير متوفّر حاليّاً بكمية أكثر
                </div>
              )}

              {/* Discount / rewards badge — shown ONLY when this product
                  actually earns rewards for the customer AND the customer
                  has a tier that grants rewards (>0). Products marked
                  `excludes_rewards=true` are silent. */}
              {rewardPerItem > 0 && !product.excludes_rewards && (
                <div
                  data-testid="modal-reward-badge"
                  className="bg-[#FEE2E2] border border-[#E85D45]/40 text-[#B91C1C] rounded-xl px-3 py-2 text-xs sm:text-sm text-center font-bold flex items-center justify-center gap-2"
                >
                  <Gift className="w-4 h-4" />
                  <span>
                    🎁 خصم لك:
                    <span className="mx-1" dir="ltr">
                      {(rewardPerItem * quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                    </span>
                    د.ع
                    {quantity > 1 && (
                      <span className="opacity-70 mr-1 text-[10px]" dir="ltr">
                        ({rewardPerItem} × {quantity})
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Total */}
              <div className="flex items-center justify-between py-2 border-t border-[#E4EBE6]">
                <span className="text-sm text-[#5C6E64]">المجموع</span>
                <span
                  data-testid="modal-total"
                  className="font-bold text-xl sm:text-2xl text-[#E85D45]"
                  dir="ltr"
                >
                  {(product.price * quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع
                </span>
              </div>

              {/* Add to Cart */}
              <motion.button
                ref={buyBtnRef}
                whileHover={remainingStock > 0 ? { scale: 1.02 } : {}}
                whileTap={remainingStock > 0 ? { scale: 0.95 } : {}}
                onClick={handleAdd}
                disabled={remainingStock === 0}
                data-testid="modal-add-to-cart"
                className={`w-full font-bold py-3 sm:py-4 rounded-full transition-colors flex items-center justify-center gap-2 ${
                  remainingStock === 0
                    ? "bg-[#BAE6FD] text-[#5C6E64] cursor-not-allowed"
                    : "bg-[#E85D45] hover:bg-[#d54d35] text-white"
                }`}
              >
                <ShoppingBag className="w-5 h-5" />
                {remainingStock === 0 ? "غير متوفّر" : "اشتري"}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductModal;
